import { EnhancedWithAuthHttpService } from './http-auth.service'
import { axiosInstance } from './http-common.service'
import { HttpService } from './http.service'

import {
  AccountMatchResult,
  IConsumerScanInfoRespDto,
  IConsumerScansRespDto,
  IConsumerScanUpdateStatusDto,
  IMessageResponseDto,
  IScanToEarnCreateAccountOrProductDto,
  IScanToEarnCreateDtoWithUploadedMedia,
  IScanToEarnFindImpactsDto,
  IScanToEarnFindImpactsRespDto,
  IScanToEarnGetInformationDto,
  IScanToEarnGetInformationRespDto,
} from 'src/types'

class ScanToEarnService extends EnhancedWithAuthHttpService {
  constructor() {
    super(new HttpService(axiosInstance))
  }

  getScanInformation(dto: IScanToEarnGetInformationDto) {
    const formData = new FormData()

    formData.append('barcode_id', dto.barcode_id)

    dto.mediaFiles.forEach((file) => {
      formData.append('files', file, file.name)
    })

    return this.post<IScanToEarnGetInformationRespDto, FormData>(
      `scan-to-earn/information`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
  }

  findImpacts(dto: IScanToEarnFindImpactsDto) {
    return this.post<IScanToEarnFindImpactsRespDto, IScanToEarnFindImpactsDto>(
      `scan-to-earn/impacts`,
      dto,
    )
  }

  createConsumerScan(dto: IScanToEarnCreateDtoWithUploadedMedia) {
    return this.post<IMessageResponseDto, IScanToEarnCreateDtoWithUploadedMedia>(
      `scan-to-earn/create`,
      dto,
      {},
    )
  }

  getConsumerScans(params?: {}) {
    return this.get<IConsumerScansRespDto>(`scan-to-earn`, { params })
  }

  getConsumerScan(id: string) {
    return this.get<IConsumerScanInfoRespDto>(`scan-to-earn/consumer-scan/${id}`, {})
  }

  getConsumerScansScanners() {
    return this.get<{ id: string; name: string; email: string }[]>(`scan-to-earn/scanners`)
  }

  getConsumerScansProductTypes() {
    return this.get<string[]>(`scan-to-earn/product-types`)
  }

  getNotRecognizedImpactsCsv() {
    return this.get<any>('scan-to-earn/new-impacts-csv')
  }

  validateBarcodeId(barcodeId: string) {
    return this.post<
      {
        messageFeedback: string
        isValid: boolean
      },
      { barcode_id: string }
    >('scan-to-earn/validate', {
      barcode_id: barcodeId,
    })
  }

  removePendingConsumerScans(ids: string[]) {
    return this.delete<IMessageResponseDto>(`scan-to-earn/`, { data: { ids } })
  }

  updateConsumerScanStatus(dto: IConsumerScanUpdateStatusDto) {
    let data: Pick<
      IConsumerScanUpdateStatusDto,
      'decline_reason' | 'status' | 'approval_feedbacks'
    > = {
      status: dto.status,
    }

    if (dto?.decline_reason) {
      data.decline_reason = dto.decline_reason
    }
    if (dto?.approval_feedbacks) {
      data.approval_feedbacks = dto.approval_feedbacks
    }

    return this.put<IMessageResponseDto, Omit<IConsumerScanUpdateStatusDto, 'id'>>(
      `scan-to-earn/${dto.id}/status`,
      data,
    )
  }

  createAIAccountAndProduct(dto: IScanToEarnCreateAccountOrProductDto) {
    return this.post<IMessageResponseDto, IScanToEarnCreateAccountOrProductDto>(
      `scan-to-earn/create-entities`,
      dto,
    )
  }

  getExistingAccountsForConsumerScan(consumerScanId?: string) {
    if (consumerScanId) {
      return this.get<AccountMatchResult>(`scan-to-earn/accounts/${consumerScanId}`)
    }
  }
}

export const scanToEarnService = new ScanToEarnService()
